import { DEFAULT_CURRENCY, getMonthName } from "@repo/shared";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { useConfirm } from "@/hooks/use-confirm";
import type { WithdrawalFormData, WithdrawalItem } from "@/lib/api/flow-instances/withdrawals";
import {
  useCreateWithdrawal,
  useDeleteWithdrawal,
  useUpdateWithdrawal,
  useWithdrawals,
} from "@/lib/hooks/use-flow-instances";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { WealthPlanDialog } from "./wealth-plan-dialog";
import { T } from "./wealth-plan-widget.i18n";

type WealthPlanWidgetProps = {
  flowInstanceId: string;
  currency?: string;
  defaultPeriodYears?: number;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
};

/**
 * Format currency amount
 */
type FormatNumberFn = (
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
) => string;

function formatCurrency(amount: number, currency: string, fmt: FormatNumberFn): string {
  return `${fmt(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`;
}

/**
 * Format a date for display
 */
function formatDate(date: Date | string, language: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return `${getMonthName(d.getMonth(), language)} ${d.getFullYear()}`;
}

/**
 * Get frequency label
 */
function _getFrequencyLabel(frequency: string | null): string {
  switch (frequency) {
    case "monthly":
      return "/month";
    case "quarterly":
      return "/quarter";
    case "yearly":
      return "/year";
    default:
      return "";
  }
}

export function WealthPlanWidget({
  flowInstanceId,
  currency = DEFAULT_CURRENCY,
  defaultPeriodYears = 10,
  disabled = false,
  style = "border",
  title,
  description,
}: WealthPlanWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);
  const { withdrawals } = useWithdrawals(flowInstanceId);
  const createWithdrawal = useCreateWithdrawal(flowInstanceId);
  const updateWithdrawal = useUpdateWithdrawal(flowInstanceId);
  const deleteWithdrawal = useDeleteWithdrawal(flowInstanceId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWithdrawal, setEditingWithdrawal] = useState<WithdrawalItem | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const isPending =
    createWithdrawal.isPending || updateWithdrawal.isPending || deleteWithdrawal.isPending;

  const handleAdd = () => {
    setEditingWithdrawal(null);
    setDialogOpen(true);
  };

  const handleEdit = (withdrawal: WithdrawalItem) => {
    setEditingWithdrawal(withdrawal);
    setDialogOpen(true);
  };

  const handleDelete = async (withdrawal: WithdrawalItem) => {
    const confirmed = await confirm({
      title: t.deleteWithdrawal,
      description: t.deleteConfirmation(withdrawal.name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    try {
      const result = await deleteWithdrawal.mutateAsync(withdrawal.id);
      if (!result.success) {
        await confirm({
          title: t.error,
          description: result.error || t.failedToDelete,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    } catch (err: any) {
      await confirm({
        title: t.error,
        description: err?.message || t.failedToDelete,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  const handleSave = async (
    data: WithdrawalFormData,
  ): Promise<{ success: boolean; error?: string }> => {
    if (editingWithdrawal) {
      return updateWithdrawal.mutateAsync({ withdrawalId: editingWithdrawal.id, data });
    }
    return createWithdrawal.mutateAsync(data);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingWithdrawal(null);
  };

  // Get container class based on style
  const getContainerClass = () => {
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  const getFrequencyLabelTranslated = (frequency: string | null): string => {
    switch (frequency) {
      case "monthly":
        return t.perMonth;
      case "quarterly":
        return t.perQuarter;
      case "yearly":
        return t.perYear;
      default:
        return "";
    }
  };

  const renderWithdrawalSummary = (withdrawal: WithdrawalItem) => {
    if (withdrawal.scheduleType === "one_time") {
      const amount = withdrawal.oneTimeAmount || 0;
      const date = withdrawal.oneTimeDate;
      return (
        <span className="text-sm text-muted-foreground">
          {t.oneTime}: {formatCurrency(amount, currency, formatNumber)}
          {date && ` ${t.on} ${formatDate(date, userLanguage)}`}
        </span>
      );
    } else {
      const amount = withdrawal.recurringAmount || 0;
      const freq = getFrequencyLabelTranslated(withdrawal.recurringFrequency);
      const start = withdrawal.recurringStartDate;
      const end = withdrawal.recurringEndDate;
      return (
        <div className="space-y-0.5">
          <span className="text-sm text-muted-foreground">
            {t.recurring}: {formatCurrency(amount, currency, formatNumber)}
            {freq}
            {start && ` ${t.from} ${formatDate(start, userLanguage)}`}
          </span>
          {!end && (
            <p className="text-xs text-muted-foreground/70">{t.noEndDate(defaultPeriodYears)}</p>
          )}
          {end && (
            <p className="text-xs text-muted-foreground/70">
              {t.until} {formatDate(end, userLanguage)}
            </p>
          )}
        </div>
      );
    }
  };

  return (
    <>
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />

        {/* Withdrawal List */}
        {withdrawals.length === 0 ? (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">{t.noWithdrawals}</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {withdrawals.map((withdrawal) => (
              <Card key={withdrawal.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{withdrawal.name}</div>
                    {renderWithdrawalSummary(withdrawal)}
                  </div>
                  {!disabled && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.edit}
                        onClick={() => handleEdit(withdrawal)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.delete}
                        onClick={() => handleDelete(withdrawal)}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add Button */}
        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={isPending}
            className="w-full"
          >
            {t.addWithdrawal}
          </Button>
        )}
      </div>

      <WealthPlanDialog
        open={dialogOpen}
        withdrawal={editingWithdrawal}
        defaultPeriodYears={defaultPeriodYears}
        onClose={handleDialogClose}
        onSave={handleSave}
      />

      <ConfirmationDialog />
    </>
  );
}
